"use client";

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, type Team, type TeamMember } from "@/lib/api";

export function useTeams() {
	return useQuery<Team[]>({
		queryKey: ["teams"],
		queryFn: () => api.getTeams(),
	});
}

export function useActiveTeam() {
	const queryClient = useQueryClient();

	const { data, isPending, error } = useQuery<{ team: Team | null }>({
		queryKey: ["activeTeam"],
		queryFn: () => api.getActiveTeam(),
	});

	const setActiveTeamMutation = useMutation({
		mutationFn: (teamId: string | null) => api.setActiveTeam(teamId),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["activeTeam"] });
		},
	});

	const setActiveTeam = async (teamId: string | null) => {
		return setActiveTeamMutation.mutateAsync(teamId);
	};

	return {
		activeTeam: data?.team || null,
		isLoading: isPending,
		error,
		setActiveTeam,
		isSettingTeam: setActiveTeamMutation.isPending,
	};
}

export function useCreateTeam() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (data: {
			name: string;
			slug?: string;
			description?: string;
			planType?: string;
		}) => api.createTeam(data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["teams"] });
			queryClient.invalidateQueries({ queryKey: ["activeTeam"] });
		},
	});
}


